/**
 * Buffers and curves every audio layer draws on. Generated once from the
 * seeded RNG at boot, then shared — a noise burst is an `AudioBufferSourceNode`
 * reading this material from a random offset, which is far cheaper than
 * synthesising fresh noise per sound.
 */

import type { RNG } from '../core/types';

/**
 * Seconds of noise held in each shared buffer. Long enough that reading it from
 * a random offset never sounds like the same loop twice.
 */
export const NOISE_SECONDS = 4;

/** Flat-spectrum noise. The raw material for footsteps, splashes and clicks. */
export function makeWhiteBuffer(ctx: BaseAudioContext, rng: RNG, seconds = NOISE_SECONDS): AudioBuffer {
  const buf = ctx.createBuffer(2, Math.max(1, Math.floor(ctx.sampleRate * seconds)), ctx.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < d.length; i++) d[i] = rng.next() * 2 - 1;
  }
  return buf;
}

/**
 * -3dB/octave noise. Room tone and air are pink, not white; white noise reads
 * as a hiss artefact rather than as a space. Paul Kellet's filter approximation.
 */
export function makePinkBuffer(ctx: BaseAudioContext, rng: RNG, seconds = NOISE_SECONDS): AudioBuffer {
  const buf = ctx.createBuffer(2, Math.max(1, Math.floor(ctx.sampleRate * seconds)), ctx.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    let b3 = 0;
    let b4 = 0;
    let b5 = 0;
    let b6 = 0;
    for (let i = 0; i < d.length; i++) {
      const w = rng.next() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      const out = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
      b6 = w * 0.115926;
      d[i] = out * 0.11;
    }
  }
  return buf;
}

/** -6dB/octave noise. Rumble, HVAC bodies and the low half of a growl. */
export function makeBrownBuffer(ctx: BaseAudioContext, rng: RNG, seconds = NOISE_SECONDS): AudioBuffer {
  const buf = ctx.createBuffer(2, Math.max(1, Math.floor(ctx.sampleRate * seconds)), ctx.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const w = rng.next() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5;
    }
  }
  return buf;
}

/**
 * Soft-clip transfer curve for the `WaveShaper` on growls and screams. Normalised
 * so it saturates without changing the perceived level of quiet input.
 */
export function makeShaperCurve(drive: number, samples = 2048): Float32Array<ArrayBuffer> {
  const c = new Float32Array(samples);
  const norm = Math.tanh(drive);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    c[i] = Math.tanh(x * drive) / norm;
  }
  return c;
}

/**
 * Build a `PeriodicWave` from a list of sine-partial amplitudes, starting at the
 * fundamental. One oscillator with the right partials is much cheaper than a
 * stack of sines, which matters for the hum since it never stops.
 */
export function makeHarmonicWave(ctx: BaseAudioContext, partials: readonly number[]): PeriodicWave {
  const real = new Float32Array(partials.length + 1);
  const imag = new Float32Array(partials.length + 1);
  for (let i = 0; i < partials.length; i++) imag[i + 1] = partials[i]!;
  return ctx.createPeriodicWave(real, imag, { disableNormalization: false });
}
